import logger from '../utils/logger';
import { getErrorMessage, UserFriendlyError } from '../utils/errorMessages';

export type ApiResponse<T = any> = {
  status: 'SUCCESS' | 'ERROR';
  statusCode: number;
  statusMessage: string;
  error: Error | null | unknown;
  result: T | null;
  userFriendlyError?: UserFriendlyError;
};

export type BasicFetchResponse<T = any> = {
  status: number;
  statusText: string;
  data: T;
};

function isBasicFetchResponse<T>(err: unknown): err is BasicFetchResponse<T> {
  return (
    typeof err === 'object' &&
    err !== null &&
    'status' in err &&
    typeof (err as BasicFetchResponse<T>).status === 'number' &&
    'statusText' in err &&
    typeof (err as BasicFetchResponse<T>).statusText === 'string' &&
    'data' in err
  );
}

const successResponse = <T>(response: BasicFetchResponse<T>): ApiResponse<T> => {
  return {
    status: 'SUCCESS',
    statusCode: response.status,
    statusMessage: response.statusText,
    error: null,
    result: response.data as T,
  };
};

const errorResponse = (response: BasicFetchResponse, errMsg: any): ApiResponse => {
  const userFriendlyError = getErrorMessage(response.status);
  return {
    status: 'ERROR',
    statusCode: response.status,
    statusMessage: errMsg,
    error: new Error(errMsg),
    result: null,
    userFriendlyError,
  };
};

const handleError = (err: unknown): ApiResponse => {
  let message, error;
  if (err instanceof Error) {
    error = err as Error;
    logger.error('API error handler caught exception', {
      errorMessage: error.message,
      errorName: error.name,
      stackTrace: error.stack,
    });
    return errorResponse(
      {
        status: 500,
        statusText: 'Internal Server Error',
      } as BasicFetchResponse,
      error.message
    );
  }
  if (isBasicFetchResponse(err)) {
    logger.error('API error handler caught fetch response error', {
      statusCode: err.status,
      statusText: err.statusText,
      responseData: err.data,
    });
    return errorResponse(err as BasicFetchResponse, err.statusText);
  } else {
    message = String(err);
  }
  logger.error('API error handler caught unknown error', {
    error: message,
  });
  return errorResponse({ status: 500, statusText: 'Internal Server Error' } as BasicFetchResponse, message);
};

export const handleApiResponse = async <T>(apiCall: () => Promise<ApiResponse<T>>): Promise<T> => {
  const response = await apiCall();
  if (response.statusCode === 403 || response.statusCode === 401) {
    logger.error('Authorization failed', {
      statusCode: response.statusCode,
      statusMessage: response.statusMessage,
    });
    throw new Error(`Authorization failed.
    ${response.statusMessage}`);
  }
  if (response.statusCode > 399) {
    logger.error('API response error', {
      statusCode: response.statusCode,
      statusMessage: response.statusMessage,
      result: response.result,
    });
    throw new Error(response.statusMessage, {
      cause: {
        statusCode: response.statusCode,
        message: (response.result as any).message,
      },
    });
  }

  if (response.status === 'ERROR') {
    logger.error('API response status error', {
      statusMessage: response.statusMessage,
      error: response.error,
    });
    throw new Error(`Error: ${response.statusMessage}`);
  }
  return response.result as T;
};

const defaultExport = {
  Success: successResponse,
  Error: errorResponse,
  HandleError: handleError,
  handleApiResponse: handleApiResponse,
};

export default defaultExport;
